import { CloudSun } from 'lucide-react';
import { useAwareness } from '../../awareness/store';
import type { Antenna } from '../../types';
import { useClock } from '@/state/store';

/** Forecast context is separate from geometric visibility and receiver telemetry. */
export function StationWeather({ antenna }: { antenna: Antenna }) {
  const impact = useAwareness((s) => s.weatherImpacts?.[antenna.id]);
  const checkedAt = useAwareness((s) => s.weatherImpactsMs);
  const live = useClock((s) => s.live);
  const isForecast = live && impact?.source === 'live';
  const factor = isForecast ? impact.impactFactor : antenna.performance?.weatherImpactFactor;
  return (
    <div
      className="rounded-control border border-hairline bg-inset/50 p-3 text-xs leading-relaxed"
      data-testid="station-weather"
    >
      <div className="mb-1 flex items-center gap-1.5 font-medium text-ink">
        <CloudSun className="h-3.5 w-3.5" /> Station weather
      </div>
      {isForecast ? (
        <>
          <p className="text-ink-2">
            Forecast cloud cover: {impact.cloudCover ?? 'Unknown'}% · precipitation probability:{' '}
            {impact.precipProb ?? 'Unknown'}%
          </p>
          <p className="text-[10px] text-ink-3">
            NWS forecast{checkedAt ? ` · fetched ${new Date(checkedAt).toISOString().slice(11, 16)} UTC` : ''}
          </p>
        </>
      ) : (
        <p className="text-ink-3">
          Current forecast unavailable or outside live mode. Using configured planning conditions.
        </p>
      )}
      {factor !== undefined && (
        <p className="mt-1 text-ink-2">Estimated weather planning impact: {Math.round(factor * 100)}%</p>
      )}
      <p className="mt-1 text-[10px] text-ink-3">
        {antenna.capabilities.bands.includes('Optical')
          ? 'The optical estimate uses cloud cover.'
          : 'The RF estimate uses precipitation probability.'}{' '}
        This is a simplified planning factor, not measured link margin or geometric loss of signal.
      </p>
    </div>
  );
}
